import React, { forwardRef, useEffect, useState, useRef } from "react";
import ReportLayout from "../shared/ReportLayout.tsx";
import API from "../../../api/api.js";
import SurveyReportBody, {
  JobData,
  LocationAndDepthDetails,
  EquipmentDetails,
  SurveyDetails,
  TieOnDetails,
} from "./SurveyReportBody.tsx";
import DocumentFooter from "../shared/Footer.tsx";
import GyroscopicSurveyHeader from "./headers/GyroscopicSurveyHeader.tsx";
import SurveyDynamics from "./SurveyDynamics.tsx";

interface SurveyCalculationReportProps {
  scale?: number;
  jobId: number;
  wellId: number;
  runId: number;
}

const SurveyCalculationReport = forwardRef<HTMLDivElement, SurveyCalculationReportProps>(
  ({ scale = 1, jobId, wellId, runId }, ref) => {
    const [jobData, setJobData] = useState<JobData | null>(null);
    const [locationDepthData, setLocationDepthData] = useState<LocationAndDepthDetails | null>(null);
    const [surveyRowDetails, setSurveyRowDetails] = useState<any[]>([]);
    const [surveyDetails, setSurveyDetails] = useState<SurveyDetails | null>(null);
    const [tieOnDetails, setTieOnDetails] = useState<TieOnDetails | null>(null);
    const [loading, setLoading] = useState(true);
    const pageRef = useRef<HTMLDivElement>(null);
    const rowRef = useRef<HTMLDivElement>(null);
    const [rowsPerPage, setRowsPerPage] = useState(35);

    const staticJobData: JobData = {
      job_reference: "JOB-12345",
      date_of_survey: "2025-09-10",
      well_name: "Well-A1",
      rig_no: "Rig-007",
      service: "Directional Drilling Survey",
    };

    const staticLocationDepth: LocationAndDepthDetails = {
      latitude_1: 28, latitude_2: 35, latitude_3: 45.678, latitude_unit: "N",
      longitude_1: 56, longitude_2: 12, longitude_3: 34.567, longitude_unit: "E",
      northing: "10000.0000", northing_unit: "m",
      easting: "100000.00000", easting_unit: "m",
      geodetic_system: "Univeral Transverse Mercator",
      geodetic_datum: "PSD 93",
      map_zone: "Zone 40N(54E to 60E)",
      north_reference: "Grid north",
      grid_correction: "25.62 degs",
      grid_correction_unit: "deg",
      central_meridian: "25 E", central_meridian_unit: "deg",
      elevation_referred_to: "Mean Sea Level(MSL)",
      ground_level: 100, ground_level_unit: "m",
      reference_datum: "RKB/DFE",
      reference_height: "15.00", reference_height_unit: "m",
      reference_elevation: "25.00", reference_elevation_unit: "m",
      survey_calculation_method: "Minimum Curvature",
    };

    const staticEquipment: EquipmentDetails = {
      survey_probe_type1: "Gyro-1000",
      survey_probe_type2: "Accelerometer-2000",
      probe_id1: "G-1000",
      probe_id2: "A-2000",
      error_model1: "Model A",
      error_model2: "Model B",
      displacement_referred_to: "Bit",
      vertical_section_reference: "Rotary Table",
      vertical_section_azimuth: 120,
      vertical_section_azimuth_unit: "deg",
    };

    useEffect(() => {
      const fetchSurveyData = async () => {
        try {
          setLoading(true);
          const res = await API.get(`/report/${jobId}/${wellId}/${runId}/`);
          const data = res.data;
          
          if (data.job) {
            setJobData({
              job_reference: data.job.job_number || "-",
              date_of_survey: data.job.start_date || "-",
              well_name: data.job.well_name || "-",
              rig_no: data.job.rig_number || "-",
              service: data.job.service_name || "-",
            });
          }
          if (data.well) {
            setLocationDepthData({
              latitude_1: data.well.latitude_degrees,
              latitude_2: data.well.latitude_minutes,
              latitude_3: data.well.latitude_seconds,
              latitude_unit: "N",
              longitude_1: data.well.longitude_degrees,
              longitude_2: data.well.longitude_minutes,
              longitude_3: data.well.longitude_seconds,
              longitude_unit: "E",
              northing: data.well.northing_value,
              northing_unit: "m",
              easting: data.well.easting_value,
              easting_unit: "m",
              geodetic_system: data.well.geodetic_system,
              geodetic_datum: data.well.geodetic_datum,
              map_zone: data.well.map_zone,
              north_reference: data.well.north_reference,
              grid_correction: data.well.get_grid_correction,
              grid_correction_unit: "deg",
              central_meridian: data.well.central_meridian,
              central_meridian_unit: "deg",
              elevation_referred_to: data.well.elevation_referred_to,
              ground_level: data.well.ground_level_elevation,
              ground_level_unit: "m",
              reference_datum: data.well.reference_datum,
              reference_elevation: data.well.reference_elevation || 0,
              reference_elevation_unit: "m",
              survey_calculation_method: data.job.survey_method || "Minimum Curvature",
            });
          }
          if (data.run) {
            setSurveyDetails({
              well_type: data.run.well_type,
              hole_section: data.run.hole_section,
              survey_run_in: data.run.survey_run_in,
              minimum_id: data.run.minimum_id,
              survey_tool_type: data.run.survey_tool_type,
              survey_performed_in: data.run.survey_performed_in,
              survey_from: data.run.survey_from || 0,
              survey_from_unit: "m",
              survey_to: data.run.survey_to || 0,
              survey_to_unit: "m",
              distance_unit: data.run.distance_unit,
              survey_footage: data.run.survey_meterage,
              reference_height: data.run.reference_height,
              reference_height_unit: "m",
              drill_floor_elevation: data.run.drill_floor_elevation || 0,
              drill_floor_elevation_unit: "m",
              run_name: data.run.run_name
            });
          }

          if (data.interpolation_rows && data.interpolation_rows.length > 0) {
  // Use all interpolation data as it comes from the API
  const mappedRows = data.interpolation_rows.map((row: any) => ({
    measured_depth: row.depth || "-",
    inclination: row.inclination || "-",
    azimuth: row.azimuth || "-",
    tvd: row.tvd || "-",
    Vertical_Section: row.vertical_section || "-",
    latitude: row.ns || "-",
    departure: row.ew || "-",
    DLS: row.dls || "-",
  }));
  setSurveyRowDetails(mappedRows);
} else if (data.survey_rows && data.survey_rows.length > 0) {
  // Fallback to normal survey rows when no interpolation data
  const mappedRows = data.survey_rows.map((row: any) => ({
    measured_depth: row.depth || "-",
    inclination: row.inclination || "-",
    azimuth: row.azimuth || "-",
    tvd: row.tvd || "-",
    Vertical_Section: row.Vertical_Section || "-",
    latitude: row.latitude || "-",
    departure: row.departure || "-",
    DLS: row.DLS || "-",
  }));
  setSurveyRowDetails(mappedRows);
}

          if (data.tie_on) {
            setTieOnDetails({
              measured_depth: data.tie_on.md || 0,
              measured_depth_unit: "m",
              inclination: data.tie_on.inc || 0,
              inclination_unit: "deg",
              azimuth: data.tie_on.az || 0,
              azimuth_unit: "deg",
              true_vertical_depth: data.tie_on.tvd || 0,
              true_vertical_depth_unit: "m",
              latitude: data.tie_on.ns,
              latitude_unit: "m",
              departure: data.tie_on.ew,
              depature_unit: "m",
              data_obtained_by: "Survey Engineer",
            });
          }
        } catch (error) {
          console.error("Error fetching survey report data:", error);
        } finally {
          setLoading(false);
        }
      };

      fetchSurveyData();
    }, [jobId, wellId, runId]);

    useEffect(() => {
      if (pageRef.current && rowRef.current) {
        const A4_HEIGHT_MM = 297; 
        const MARGIN_MM = 20; 
        const CONTENT_HEIGHT_MM = A4_HEIGHT_MM - 2 * MARGIN_MM;

        const headerHeight = 30; 
        const footerHeight = 20; 
        const rowHeight = rowRef.current.getBoundingClientRect().height / 3.78; 

        const availableHeight = CONTENT_HEIGHT_MM - headerHeight - footerHeight;
        const dynamicRowsPerPage = Math.floor(availableHeight / rowHeight);
        setRowsPerPage(dynamicRowsPerPage > 0 ? dynamicRowsPerPage : 1);
      }
    }, [surveyRowDetails]);

    const calculateIndex = (sur_lengths: number[], index: number, offset: number) => {
      let sum = 0;
      for (let i = 0; i <= index + offset; i++) sum += sur_lengths[i] || 0;
      return sum;
    };

    const totalPages = Math.ceil(surveyRowDetails.length / rowsPerPage);
    const getPageRows = (pageIndex: number) =>
      surveyRowDetails.slice(pageIndex * rowsPerPage, (pageIndex + 1) * rowsPerPage);

    if (loading) {
      return <div>Loading survey data...</div>;
    }

    return (
      <div ref={ref}>
        <ReportLayout
          scale={scale}
          headerProps={{
            type: "Survey Report",
            department: "Directional Drilling",
            scope: "Internal",
            issue_date: new Date().toISOString(),
            document_ref_no: `SUR-${jobData?.job_reference || staticJobData.job_reference}`,
            title: "Survey Calculation Report",
            sub_title: `Well: ${jobData?.well_name || staticJobData.well_name}, Run: Run-01`,
            rev: "0",
            rev_date: new Date().toISOString(),
          }}
          footerProps={{
            title: "Directional Drilling",
            subject: "Survey Calculation",
            category: "Technical",
            pageNo: 1,
            totalPageNo: totalPages + 1,
          }}
        >
          <SurveyReportBody
            jobdata={jobData || staticJobData}
            location_and_depth_details={locationDepthData!}
            equipment_details={staticEquipment}
            survey_details={surveyDetails}
            tie_on_details={tieOnDetails}
          />
          
        </ReportLayout>
        {Array.from({ length: totalPages }, (_, pageIndex) => (
          
          <div
            key={pageIndex}
            
            ref={pageRef}
            className="page bg-white shadow-lg  rounded-lg p-8  flex flex-col"
            style={{
              width: "210mm",
              height: "297mm",    
              boxSizing: "border-box",
              transformOrigin: "top left",
              overflow: "hidden",
              pageBreakInside: "avoid", 
              
            }}
          >
            <div className="flex flex-col ml-[5px]" style={{ width: "100%", maxWidth: "800.85px", marginLeft: "0.2", marginRight: "auto" }}>
              <GyroscopicSurveyHeader
                jobDetails={{
                  dateOfSurvey: jobData?.date_of_survey || staticJobData.date_of_survey,
                  wellName: jobData?.well_name || staticJobData.well_name,
                }}
              />
            </div>
            <div className="flex-1 mt-2">
              <SurveyDynamics
                row_length={getPageRows(pageIndex).length}
                sur_lengths={[getPageRows(pageIndex).length]}
                survey_row_details={getPageRows(pageIndex)}
                job_details={jobData || staticJobData}
                footer_data={{}}
                calculateIndex={calculateIndex}
                pageNo={pageIndex + 2}
              >
                {pageIndex === 0 && (
                  <div ref={rowRef} style={{ visibility: "hidden", height: "0" }}>
                    <SurveyDynamics
                      row_length={1}
                      sur_lengths={[1]}
                      survey_row_details={[surveyRowDetails[0] || {}]}
                      job_details={jobData || staticJobData}
                      footer_data={{}}
                      calculateIndex={calculateIndex}
                      pageNo={pageIndex + 2}
                    />
                  </div>
                )}
              </SurveyDynamics>
            </div>
              <div className="mt-auto " style={{ width: "100%" }}>
              <DocumentFooter pageNo={pageIndex + 2}></DocumentFooter>
            </div>
          </div>
        ))}
      </div>
    );
  }
);

export default SurveyCalculationReport;